import React, { useState, useMemo } from 'react';
import TopNavbar from '../home/TopNavbar';

const studyRoadmaps = {
  '6M': {
    title: '6-Month Intensive Crash Plan',
    target: 'High-Impact Revision + 99+ Percentile Push',
    dailyHours: 12,
    phases: [
      {
        id: '6m-p1',
        phase: 'Phase 1 (Months 1–3): High-Weightage Core',
        focus: 'Focus on >4% weightage chapters across PCM. Clear formulas & NCERT line-by-line for chemistry.',
        tasks: [
          'Master Physics High-Weightage: Current Electricity, Optics, Modern Physics',
          'Master Chemistry High-Weightage: Aldehydes/Ketones, Coordination, Equilibrium, s/d/f-blocks',
          'Master Maths High-Weightage: 3D Geometry, Vectors, Definite Integration, Sequence & Series, Limits/Differentiability',
          'Solve last 3 years PYQs chapter-wise immediately after chapter completion'
        ]
      },
      {
        id: '6m-p2',
        phase: 'Phase 2 (Month 4–5): Secondary & Integration',
        focus: 'Cover remaining 2-4% weightage chapters, start sectional timed practice.',
        tasks: [
          'Complete remaining medium/low weightage chapters',
          'Alternate days: 1 Physics/Chem full section test + 1 Maths section test',
          'Maintain an error notebook for conceptual or calculation blunders'
        ]
      },
      {
        id: '6m-p3',
        phase: 'Phase 3 (Month 6): Full Mocks & Simulation',
        focus: '3 hours real exam simulation, revision of formula sheets, zero new topics.',
        tasks: [
          'Attempt 2 full-length NTA pattern mock tests per week (morning/evening slots)',
          'Spend 3 hours post-mock analyzing lost marks (silly mistakes vs conceptual gaps)',
          'Revise short notes & name reactions in Chemistry daily (45 mins)'
        ]
      }
    ]
  },
  '1Y': {
    title: '1-Year Comprehensive Plan',
    target: 'Balanced Syllabus Coverage + Deep Problem Solving',
    dailyHours: 8,
    phases: [
      {
        id: '1y-p1',
        phase: 'Phase 1 (Months 1–6): Class XI & XII Parallel Sync',
        focus: 'Deep conceptual build, standard reference books / coaching module Level-1 & Level-2.',
        tasks: [
          'Concept lecture/self-study -> NCERT reading -> Module Level 1/2 execution',
          'Weekly revision tests on Sunday covering past 2 weeks’ syllabus',
          'Dedicated weekly backlog buffer day (Sunday evening/Saturday night)'
        ]
      },
      {
        id: '1y-p2',
        phase: 'Phase 2 (Months 7–9): Advanced Problem Solving & Consolidation',
        focus: 'Intensive practice of multi-concept numericals, PYQ tagging.',
        tasks: [
          'Complete PYQ compilation of last 5 years (2021–2025/2026)',
          'Identify weak sub-topics via part-tests and re-study core notes',
          'Short notes creation for organic name reactions, physics formulas, math shortcuts'
        ]
      },
      {
        id: '1y-p3',
        phase: 'Phase 3 (Months 10–12): Mock Blitz & Peak Performance',
        focus: 'Exam temperament conditioning and high-speed mock drills.',
        tasks: [
          '3 full mock tests per week with strict OMR/NTA CBT interface practice',
          'Revise high-weightage chapters 3 times in final 45 days',
          'Sleep cycle alignment with 9:00 AM – 12:00 PM exam shift'
        ]
      }
    ]
  }
};

const dailyRoutine = [
  { time: '06:30 – 07:00 AM', activity: 'Wake up, hydration, light stretching / mental priming' },
  { time: '07:00 – 10:00 AM', activity: 'Block 1: Mathematics (Problem Solving & Calculus/Algebra)' },
  { time: '10:00 – 10:45 AM', activity: 'Breakfast, short walk, zero screen break' },
  { time: '10:45 – 01:45 PM', activity: 'Block 2: Physics (Conceptual theory + Numerical practice)' },
  { time: '01:45 – 02:45 PM', activity: 'Lunch & Power Nap (max 25 mins)' },
  { time: '02:45 – 05:45 PM', activity: 'Block 3: Chemistry (Organic/Inorganic memorization & calculations)' },
  { time: '05:45 – 06:30 PM', activity: 'Physical exercise / sport / refresh break' },
  { time: '06:30 – 08:30 PM', activity: 'Targeted Revision / Formula recall / NCERT line reading' },
  { time: '08:30 – 09:15 PM', activity: 'Dinner & family time' },
  { time: '09:15 – 10:30 PM', activity: 'Error log review, plan next day’s micro-targets, wind down' }
];

export default function JEEMainMobileResponsivePlannerFixed() {
  const [selectedDuration, setSelectedDuration] = useState('6M');
  const [checkedTasks, setCheckedTasks] = useState({});

  const toggleTask = (taskId, taskIndex) => {
    const key = `${taskId}-${taskIndex}`;
    setCheckedTasks((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const activePlan = studyRoadmaps[selectedDuration];

  const completionStats = useMemo(() => {
    let totalTasks = 0;
    let completedCount = 0;
    activePlan.phases.forEach((phase) => {
      phase.tasks.forEach((_, idx) => {
        totalTasks++;
        if (checkedTasks[`${phase.id}-${idx}`]) {
          completedCount++;
        }
      });
    });
    return {
      totalTasks,
      completedCount,
      percentage: totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0
    };
  }, [activePlan, checkedTasks]);

  return (
    <>
      <style>{`
        .jeesp-mobile-wrapper {
          width: 100%;
          min-height: 100vh;
          background-color: #f9fafb;
          box-sizing: border-box;
          overflow-x: hidden;
          margin-top: 50px;
          padding-bottom: 90px; /* Space for mobile floating bottom tab bars */
        }
        .jeesp-mobile-wrapper *, 
        .jeesp-mobile-wrapper *::before, 
        .jeesp-mobile-wrapper *::after {
          box-sizing: border-box;
          min-width: 0;
        }
        .jeesp-container {
          width: 100%;
          max-width: 1000px;
          margin: 0 auto;
          padding: 16px 12px;
        }
        .jeesp-header {
          text-align: center;
          margin-bottom: 16px;
        }
        .jeesp-header h1 {
          font-size: 1.35rem;
          font-weight: 800;
          color: #111827;
          margin: 0 0 4px 0;
          line-height: 1.25;
          word-break: break-word;
        }
        .jeesp-header p {
          color: #4b5563;
          font-size: 0.8rem;
          line-height: 1.4;
          margin: 0 auto;
          word-break: break-word;
        }
        .jeesp-toolbar {
          display: flex;
          flex-direction: column;
          gap: 10px;
          background: #ffffff;
          padding: 12px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          margin-bottom: 16px;
        }
        .jeesp-duration-tabs {
          display: flex;
          gap: 8px;
          width: 100%;
        }
        .jeesp-tab-btn {
          flex: 1;
          padding: 8px 10px;
          border-radius: 6px;
          font-size: 0.78rem;
          font-weight: 600;
          border: 1px solid #d1d5db;
          background: #f3f4f6;
          color: #374151;
          cursor: pointer;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .jeesp-tab-btn.active {
          background: #2563eb;
          color: #ffffff;
          border-color: #2563eb;
        }
        .jeesp-progress-pill {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 0.78rem;
          font-weight: 600;
          color: #374151;
        }
        .jeesp-mini-bar {
          flex: 1;
          max-width: 110px;
          height: 6px;
          background: #e5e7eb;
          border-radius: 3px;
          overflow: hidden;
          margin-left: 8px;
        }
        .jeesp-mini-fill {
          height: 100%;
          background: #16a34a;
          border-radius: 3px;
          transition: width 0.3s ease;
        }
        .jeesp-card {
          background: #ffffff;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          padding: 12px;
          margin-bottom: 14px;
        }
        .jeesp-card-header {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 12px;
          border-bottom: 1px solid #f3f4f6;
          padding-bottom: 8px;
        }
        .jeesp-card-header h2 {
          font-size: 1rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
          line-height: 1.3;
          word-break: break-word;
        }
        .jeesp-time-badge {
          background: #eff6ff;
          color: #1d4ed8;
          font-weight: 600;
          padding: 3px 6px;
          border-radius: 4px;
          font-size: 0.68rem;
          display: inline-block;
          white-space: nowrap;
          align-self: flex-start;
        }
        .jeesp-phase-block {
          margin-bottom: 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 10px;
        }
        .jeesp-phase-title {
          font-size: 0.88rem;
          font-weight: 700;
          color: #1e3a8a;
          margin: 0 0 4px 0;
          line-height: 1.35;
          word-break: break-word;
        }
        .jeesp-phase-focus {
          font-size: 0.75rem;
          color: #475569;
          margin: 0 0 8px 0;
          line-height: 1.35;
          word-break: break-word;
        }
        .jeesp-task-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .jeesp-task-item {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          font-size: 0.78rem;
          color: #1f2937;
          cursor: pointer;
          line-height: 1.35;
          word-break: break-word;
        }
        .jeesp-task-item input[type='checkbox'] {
          margin-top: 2px;
          cursor: pointer;
          accent-color: #2563eb;
          width: 14px;
          height: 14px;
          flex-shrink: 0;
        }
        .jeesp-task-item.completed span {
          text-decoration: line-through;
          color: #9ca3af;
        }
        .jeesp-routine-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .jeesp-routine-row {
          display: flex;
          flex-direction: column;
          gap: 3px;
          padding: 8px;
          background: #f8fafc;
          border-radius: 6px;
          border: 1px solid #f1f5f9;
        }
        .jeesp-routine-act {
          font-size: 0.78rem;
          color: #1f2937;
          line-height: 1.35;
          word-break: break-word;
        }
        .jeesp-tips-box {
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 10px;
          padding: 12px;
          color: #92400e;
          font-size: 0.78rem;
          line-height: 1.4;
        }
        .jeesp-tips-box h3 {
          margin: 0 0 6px 0;
          font-size: 0.85rem;
          font-weight: 700;
        }
        .jeesp-tips-box ul {
          margin: 0;
          padding-left: 16px;
        }
        .jeesp-tips-box li + li {
          margin-top: 4px;
        }

        /* Desktop expansion */
        @media (min-width: 640px) {
          .jeesp-mobile-wrapper {
            padding-bottom: 24px;
          }
          .jeesp-container {
            padding: 24px 16px;
          }
          .jeesp-header h1 {
            font-size: 1.75rem;
          }
          .jeesp-header p {
            font-size: 0.9rem;
          }
          .jeesp-toolbar {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }
          .jeesp-duration-tabs {
            width: auto;
          }
          .jeesp-card-header {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }
          .jeesp-routine-list {
            display: grid;
            grid-template-columns: 1fr;
          }
          .jeesp-routine-row {
            flex-direction: row;
            align-items: center;
            gap: 12px;
          }
          .jeesp-routine-act {
            flex: 1;
          }
        }
      `}</style>

      <div className="jeesp-mobile-wrapper">

        <TopNavbar name="Study Planner" ></TopNavbar>

        <div className="jeesp-container">
          <header className="jeesp-header">
            <h1>JEE Main Strategic Study Planner</h1>
            <p>Milestone roadmaps, high-yield allocation splits, and daily rhythm.</p>
          </header>

          <div className="jeesp-toolbar">
            <div className="jeesp-duration-tabs">
              <button
                className={`jeesp-tab-btn ${selectedDuration === '6M' ? 'active' : ''}`}
                onClick={() => setSelectedDuration('6M')}
              >
                6-Month Intensive
              </button>
              <button
                className={`jeesp-tab-btn ${selectedDuration === '1Y' ? 'active' : ''}`}
                onClick={() => setSelectedDuration('1Y')}
              >
                1-Year Comprehensive
              </button>
            </div>

            <div className="jeesp-progress-pill">
              <span>Progress: {completionStats.percentage}%</span>
              <div className="jeesp-mini-bar">
                <div
                  className="jeesp-mini-fill"
                  style={{ width: `${completionStats.percentage}%` }}
                />
              </div>
            </div>
          </div>

          {/* Phase Roadmap Card */}
          <div className="jeesp-card">
            <div className="jeesp-card-header">
              <h2>{activePlan.title}</h2>
              <span className="jeesp-time-badge">Target: {activePlan.target}</span>
            </div>
            {activePlan.phases.map((phase) => (
              <div key={phase.id} className="jeesp-phase-block">
                <h3 className="jeesp-phase-title">{phase.phase}</h3>
                <p className="jeesp-phase-focus"><strong>Focus:</strong> {phase.focus}</p>
                <ul className="jeesp-task-list">
                  {phase.tasks.map((task, idx) => {
                    const taskKey = `${phase.id}-${idx}`;
                    const isChecked = !!checkedTasks[taskKey];
                    return (
                      <li
                        key={idx}
                        className={`jeesp-task-item ${isChecked ? 'completed' : ''}`}
                        onClick={() => toggleTask(phase.id, idx)}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}}
                        />
                        <span>{task}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          {/* Daily Study Routine (Card stack list for mobile zero-clipping) */}
          <div className="jeesp-card">
            <div className="jeesp-card-header">
              <h2>Recommended Daily Rhythm</h2>
              <span className="jeesp-time-badge">~{activePlan.dailyHours} Hours Allocation</span>
            </div>
            <div className="jeesp-routine-list">
              {dailyRoutine.map((slot, idx) => (
                <div key={idx} className="jeesp-routine-row">
                  <span className="jeesp-time-badge">{slot.time}</span>
                  <div className="jeesp-routine-act">{slot.activity}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Golden Rules Box */}
          <div className="jeesp-tips-box">
            <h3>⚡ High-Yield Execution Rules for 99+ Percentile</h3>
            <ul>
              <li><strong>Prioritize High-Weightage First:</strong> Secure 3D Geometry, Coordination Compounds, and Current Electricity early.</li>
              <li><strong>The 3:1 Practice Ratio:</strong> Spend 3 hours solving JEE Main PYQs and mixed numericals per 1 hour theory.</li>
              <li><strong>Mock Error Analysis &gt; Mock Giving:</strong> Deep root-cause error analysis drives percentile gains.</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}